import React, { useMemo, useContext, useCallback, useState } from "react";
import { Route, Switch, useRouteMatch, useHistory } from "react-router";

export interface CrudNavigation {
  goToList(showSuccess?: boolean): void;

  goToEditForm(id: string): void;

  goToCreate(): void;
}

interface CrudContainerProps {
  children: {
    list?: (showSuccess: boolean) => React.ReactNode;
    create?: () => React.ReactNode;
    edit?: (entityId: string) => React.ReactNode;
  };
}

const CrudNavigationContext = React.createContext<CrudNavigation>({
  goToList(): void {},
  goToEditForm(): void {},
  goToCreate(): void {},
});

export function useCrudNavigation(): CrudNavigation {
  return useContext(CrudNavigationContext);
}

export default function CrudContainer({ children }: CrudContainerProps) {
  const { list, edit, create } = children;
  const match = useRouteMatch();
  const history = useHistory();
  const [showSuccess, setShowSuccess] = useState(false);
  const navigation = useMemo(
    () => ({
      goToList(success?: boolean): void {
        setShowSuccess(!!success);
        history.push(match.url);
      },
      goToEditForm(id: string): void {
        history.push(`${match.url}/edit/${id}`);
      },
      goToCreate(): void {
        history.push(`${match.url}/new`);
      },
    }),
    [match.url, history, setShowSuccess]
  );

  const createForm = useMemo(() => (create ? create() : null), [create]);
  const editForm = useCallback((id: string) => (edit ? edit(id) : null), [edit]);
  const listComponent = useMemo(() => (list ? list(showSuccess) : null), [list, showSuccess]);

  return (
    <CrudNavigationContext.Provider value={navigation}>
      <Switch>
        {create && (
          <Route path={`${match.url}/new`} exact>
            {createForm}
          </Route>
        )}
        {edit && (
          <Route path={`${match.url}/edit/:id`} exact>
            {(props: any) => editForm(props?.match!.params?.id)}
          </Route>
        )}
        {list && (
          <Route path={match.url} exact>
            {listComponent}
          </Route>
        )}
      </Switch>
    </CrudNavigationContext.Provider>
  );
}
